import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Button } from '@material-ui/core';
import { useNavigate } from 'react-router-dom';

const useStyles = makeStyles((theme) => ({
  root: {
    fontFamily: "'Inter', sans-serif",
    color: '#ffffff',
    padding: '26px 18px 0px',
    maxWidth: 860,
    margin: '0 auto',
    height: '100%',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
    textAlign: 'left',
    [theme.breakpoints.down('sm')]: { padding: '10px 8px 0px' },
  },
  hero: {
    padding: '22px 22px 18px',
    borderRadius: 14,
    border: '1px solid rgba(77, 121, 255, 0.35)',
    background: 'radial-gradient(circle at 18% 18%, rgba(77,121,255,0.35), rgba(10,14,30,0.96))',
    boxShadow: '0 0 24px rgba(77,121,255,0.14)',
    marginBottom: 18,
  },
  badge: {
    display: 'inline-block',
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: 1,
    textTransform: 'uppercase',
    color: '#9ef7c5',
    border: '1px solid rgba(158,247,197,0.4)',
    borderRadius: 999,
    padding: '3px 12px',
    marginBottom: 14,
  },
  title: {
    fontSize: '2.0rem',
    fontWeight: 900,
    letterSpacing: -0.4,
    lineHeight: 1.1,
    textShadow: '0 0 16px rgba(77, 121, 255, 0.55)',
    marginBottom: 10,
  },
  subtitle: {
    color: '#cfd8ff',
    fontSize: 14,
    lineHeight: 1.65,
    maxWidth: 720,
    marginBottom: 20,
  },
  agentGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    marginBottom: 24,
  },
  agentCard: {
    borderRadius: 12,
    border: '1px solid rgba(77, 121, 255, 0.22)',
    background: 'rgba(10, 14, 30, 0.82)',
    padding: '14px 18px',
    display: 'flex',
    alignItems: 'flex-start',
    gap: 14,
  },
  agentIcon: {
    fontSize: '1.6rem',
    lineHeight: 1,
    marginTop: 2,
    flexShrink: 0,
  },
  agentName: {
    fontSize: 14,
    fontWeight: 900,
    color: '#e5e7ff',
    marginBottom: 3,
  },
  agentDesc: {
    fontSize: 13,
    color: '#9ea4d8',
    lineHeight: 1.5,
  },
  cta: {
    borderRadius: 14,
    border: '1px solid rgba(77, 121, 255, 0.45)',
    background: 'radial-gradient(circle at 18% 18%, rgba(77,121,255,0.18), rgba(10,14,30,0.96))',
    padding: '20px 18px',
    marginBottom: 28,
    textAlign: 'center',
  },
  primaryButton: {
    backgroundColor: '#4d79ff',
    color: '#ffffff',
    textTransform: 'none',
    fontWeight: 800,
    borderRadius: 10,
    padding: '8px 16px',
    '&:hover': { backgroundColor: '#3e61cc' },
  },
}));

const AGENTS = [
  { icon: '🔭', name: 'Scout', desc: 'Scans on-chain activity for anomalies and high-signal story candidates across all monitored tokens each cycle.' },
  { icon: '🌾', name: 'Harvest', desc: 'Pulls raw transaction data, graph topology, and market context for every candidate the Scout flags.' },
  { icon: '⚠️', name: 'Risk', desc: 'Evaluates position exposure and flags cycle-level risk signals before the Manager grades execution.' },
  { icon: '⚡', name: 'Executor', desc: 'Routes qualified signals into the trading pipeline with size and timing parameters set by cycle context.' },
  { icon: '📋', name: 'Manager', desc: 'Grades every agent\'s cycle output, produces an auditable report, and adjusts agent weights for the next cycle.' },
];

export default function AgentsDirectoryPage() {
  const classes = useStyles();
  const navigate = useNavigate();

  return (
    <div className={classes.root}>
      <div className={classes.hero}>
        <div className={classes.badge}>Coming Soon</div>
        <div className={classes.title}>E3D Trading Agents</div>
        <div className={classes.subtitle}>
          Five autonomous agents running live on E3D intelligence — scanning, harvesting, managing risk, executing, and grading every cycle. Powered by the E3D story and thesis layer.
        </div>
        <Button
          className={classes.primaryButton}
          variant="contained"
          onClick={() => navigate('/pricing')}
        >
          See plans →
        </Button>
      </div>

      <div className={classes.agentGrid}>
        {AGENTS.map((a) => (
          <div key={a.name} className={classes.agentCard}>
            <div className={classes.agentIcon}>{a.icon}</div>
            <div>
              <div className={classes.agentName}>{a.name}</div>
              <div className={classes.agentDesc}>{a.desc}</div>
            </div>
          </div>
        ))}
      </div>

      <div className={classes.cta}>
        <div style={{ fontSize: '1.05rem', fontWeight: 900, color: '#e5e7ff', marginBottom: 6 }}>
          The intelligence layer for autonomous on-chain trading
        </div>
        <div style={{ color: '#cfd8ff', fontSize: 13, marginBottom: 16, lineHeight: 1.6 }}>
          When the agent integration goes live, this page will show real-time cycle grades, Manager reports, and auditable decision trails. The most compelling proof point on the site — coming soon.
        </div>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Button className={classes.primaryButton} variant="contained" onClick={() => navigate('/stories')}>
            Explore Stories →
          </Button>
          <Button
            variant="outlined"
            style={{ borderColor: 'rgba(77,121,255,0.6)', color: '#4d79ff', textTransform: 'none', fontWeight: 800, borderRadius: 10, padding: '8px 14px' }}
            onClick={() => navigate('/thesis')}
          >
            View Live Theses →
          </Button>
        </div>
      </div>
    </div>
  );
}
